import React from 'react';

import { css } from '@emotion/css';
import { Typography } from '@mui/material';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FileView } from '@/components/pdf/FileView.tsx';
import { useStyles } from '@/hooks/useStyles.ts';
import { MedicationSchema } from '@/models/ConditionModel.ts';
import { FileModel } from '@/models/FileModel.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { ControlledFileUpload } from '@/uiKit/molecules/ControlledFileInput.tsx';
import { ControlledTextField } from '@/uiKit/molecules/ControlledTextField.tsx';

export type TelemonitoringDescriptionFieldProps = {
  className?: string;
  patientInfo: {
    givenName: string;
    familyName: string;
  };
};

export const TelemonitoringDescriptionField: React.FC<
  TelemonitoringDescriptionFieldProps
> = ({ patientInfo, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const formContext = useFormContext();
  const descriptionAttachment = useWatch({ name: 'descriptionAttachment' }) as
    | FileModel
    | undefined;
  const insulinScheme = useWatch({
    name: 'insulinScheme',
  }) as MedicationSchema | undefined;
  const descriptionErrors = useFormState({
    name: 'description',
  }).errors.description;

  if (insulinScheme === 'noInsulin') {
    return null;
  }

  return (
    <div className={className}>
      <div className={styles.subtitle}>
        <Typography variant="subtitle">
          {t('diabetesForms.goalsShort')}
        </Typography>
      </div>
      <div>
        {descriptionAttachment && (
          <div className={styles.fileView}>
            <FileView
              id="tls-description-attachment-file-view"
              file={descriptionAttachment}
              onDelete={() =>
                formContext.setValue('descriptionAttachment', undefined)
              }
            />
          </div>
        )}
        <div className={styles.aside}>
          <ControlledTextField
            id="tls-description-input"
            name="description"
            multiline
          />
          <ControlledFileUpload
            id="tls-description-attachment-upload"
            name="descriptionAttachment"
            fileType="description"
            defaultValue={
              formContext.formState.defaultValues?.['descriptionAttachment']
            }
            patientInfo={patientInfo}
            displayFilename={false}
            error={!!descriptionErrors}
          />
        </div>
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  subtitle: css({
    marginBottom: theme.spacing(12),
  }),
  aside: css({
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(12),
  }),
  fileView: css({
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(4),
  }),
});
